import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Box } from "@mui/material";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, memo } from "react";
import Flex from "../../../../../components/Flex";
import LabeledSwitch from "../../../../../components/LabeledSwitch";
import SearchInput from "../../../../../components/SearchInput";
import StyledButtonV2 from "../../../../../components/app/StyledButtonV2";
import useDisclosure from "../../../../../hooks/use-disclosure";
import SendPatientAssementFormsModal from "../../patient/patient-list/patient-chart-new/assessment/PatientAssementForms/SendPatientAssementFormsModal";
import AssessmentExportButton from "./AssessmentExportButton";
import AssessmentFilter from "./AssessmentFilter";
import { AssessmentFilterData } from "./AssessmentsList";
import { useQueryParams } from "../../../../../hooks/use-query-param";

interface AssessmentEndActionsProps {
  activeAssessment: string;
  setFilters: Dispatch<SetStateAction<AssessmentFilterData>>;
  filters: AssessmentFilterData;
  setSearchStringKey?: (v: string) => void;
  searchStringKey?: string;
  rowSelectionModel?: GridRowSelectionModel;
  assessments?: any[];
  onExportAssessment?: (selected: "csv" | "pdf") => void;
  activeSubTab?: string;
  setArchive?: (v: boolean) => void;
  archive?: boolean;
}
export const AssessmentEndActions = memo(
  ({
    activeAssessment,
    setFilters,
    filters,
    setSearchStringKey,
    searchStringKey,
    rowSelectionModel = [],
    assessments = [],
    onExportAssessment,
    activeSubTab,
    setArchive,
    archive,
  }: AssessmentEndActionsProps) => {
    const sendAssementFormsDisclosure = useDisclosure();
    const activeTab = useQueryParams("activeAssessment");

    const convertRowSelectionToPatientList = (
      selectedIds: GridRowSelectionModel,
      assessments: any[]
    ): Array<{ value: string; label: string }> | undefined => {
      if (!selectedIds || !assessments || selectedIds.length === 0) {
        return undefined;
      }

      const selectedAssessments = assessments.filter((assessment) =>
        selectedIds.includes(assessment.uuid)
      );

      const uniquePatientsMap = new Map<
        string,
        { value: string; label: string }
      >();

      selectedAssessments.forEach((assessment) => {
        if (assessment.patientId && assessment.patientName) {
          if (!uniquePatientsMap.has(assessment.patientId)) {
            uniquePatientsMap.set(assessment.patientId, {
              value: assessment.patientId,
              label: assessment.patientName,
            });
          }
        }
      });

      return Array.from(uniquePatientsMap.values());
    };

    const handleOpenSendBulkAssessments = () => {
      const patientList = convertRowSelectionToPatientList(
        rowSelectionModel,
        assessments
      );

      sendAssementFormsDisclosure.open({
        patientIds: patientList,
      });
    };

    return (
      <Flex gap={1} width={"100%"} justifyContent={"flex-end"}>
        {activeSubTab === "scheduled" && (
          <LabeledSwitch
            label="Show Archived scheduled assessments"
            checked={!!archive}
            onChange={(e) => setArchive?.(e.target.checked)}
          />
        )}
        <Box width={"280px"}>
          <SearchInput
            searchKey={searchStringKey ?? ""}
            onSearch={setSearchStringKey ?? (() => {})}
            placeholder="Search by patient Name"
          />
        </Box>
        <AssessmentFilter
          activeAssessment={activeAssessment}
          onApply={(v) => setFilters(v)}
          filters={filters}
          setFilters={setFilters}
        />
        {activeTab === "sent_scheduled" && (
          <StyledButtonV2
            label={"Send Assessment"}
            sIcon={<ShareOutlinedIcon sx={{ color: "white" }} />}
            variant="contained"
            onClick={handleOpenSendBulkAssessments}
          />
        )}
        {rowSelectionModel.length > 0 && onExportAssessment && (
          <AssessmentExportButton
            onClick={onExportAssessment}
            disabled={false}
          />
        )}
        {sendAssementFormsDisclosure.opened && (
          <SendPatientAssementFormsModal
            disclosureHost={sendAssementFormsDisclosure as any}
            showPatientSelection={true}
          />
        )}
      </Flex>
    );
  }
);
